import React, { useState, useCallback } from 'react'
import { ChangeAdminReq } from '@golden/gdk-admin'
import { omitBy, isUndefined } from '@golden/utils'
import Box from '@material-ui/core/Box'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import EditManagerAccountForm, {
  EditManagerAccountFormType,
  initialForm
} from '../../../components/admin/manager/EditManagerAccountForm'
import LoadingAndErrorFrame from '../../../components/default/frames/LoadingAndErrorFrame'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import useT from '../../../i18ns/admin/useT'
import useGDK from '../../../providers/admin/gdk/useGDK'
import { usePageFlow } from '../../../utils/default/PageFlowHook'
import { useGetDataByParams, useRedirectHandleBack, useDialogHandleSubmit } from '../../../utils/default/ComplexFlowHook'
import { Path } from '../../../components/admin/route/route'
import useGlobalDialog from '../../../providers/admin/dialog/useGlobalDialog'
import { createGlobalDialogConfig } from '../../../utils/default/DialogHelper'
import { convertEmptyToUndefined } from '../../../utils/default/FormHelper'
import { parsePath } from '../../../utils/default/RouteHelper'
import { useLocation } from 'react-router'

export const formToRequest = (form: EditManagerAccountFormType): ChangeAdminReq => {
  const converted = {
    role_id: form.roleId,
    nickname: form.nickname,
    password: convertEmptyToUndefined(form.password),
    password_confirmation: convertEmptyToUndefined(form.passwordConfirmation),
    is_loginable: form.isLoginable,
    memo: form.memo
  } as ChangeAdminReq
  return omitBy(converted, isUndefined) as ChangeAdminReq
}

const ManagerAccountUpdatePage: React.FC = () => {
  const classes = useCommonStyles()
  const { t } = useT()
  const gdk = useGDK()
  const pageFlow = usePageFlow()
  const [id, setId] = useState<number>(0)
  const [defaultForm, setDefaultForm] = useState<EditManagerAccountFormType>(initialForm())

  useGetDataByParams({
    path: Path.MANAGER_ACCOUNT_UPDATE,
    gdkFunc: (param: { id: string }) => gdk.admin.getAccount(Number(param.id)),
    gdkFuncDependencies: [gdk],
    onBeforeFetch: pageFlow.setLoadingStart,
    onSuccess: (res) => {
      setId(res.id)
      setDefaultForm(initialForm({
        account: res.account,
        nickname: res.nickname,
        roleId: res.role_id,
        isLoginable: res.is_loginable,
        isGoogleAuthActive: res.is_google_auth_active,
        memo: res.memo
      }))
      pageFlow.setContentShow()
    },
    onError: pageFlow.setGDKError
  })

  const globalDialog = useGlobalDialog()
  const location = useLocation()
  const search = parsePath(location.search, location.pathname, Path.MANAGER_ACCOUNT).search as { page: number, sort_by: string, order: string }
  const [handleBack, handleDebouncedBack] = useRedirectHandleBack({ path: `${Path.MANAGER_ACCOUNT}?page=${search.page}&order=${search.order}&sort_by=${search.sort_by}` })
  const { handleSubmit } = useDialogHandleSubmit({
    formToRequest,
    gdkFunc: (payload) => gdk.admin.updateAccount(id, payload),
    gdkFuncDependencies: [gdk, id],
    dialogId: 'updateAdmin',
    globalDialog,
    getChangeDialogConfig: useCallback((form: EditManagerAccountFormType) => createGlobalDialogConfig({
      showIcon: false,
      message: (
        <React.Fragment>
          {t('dialog.confirmUpdateCompanyAccount')}
          <span className={classes.purpleWord}>{form.account}</span>
        </React.Fragment>
      )
    }), [t, classes.purpleWord]),
    getSuccessDialogConfig: useCallback(() => createGlobalDialogConfig({
      showIcon: false,
      showCancel: false,
      message: t('dialog.updateAccountSuccess')
    }), [t]),
    getFailDialogConfig: useCallback((error) => createGlobalDialogConfig({
      showIcon: true,
      showCancel: false,
      variant: 'error',
      message: error
    }), []),
    afterSuccessDialog: handleBack
  })

  return (
    <Box padding={5}>
      <Paper>
        <Box padding={4}>
          <Box
            paddingY={1.25}
            paddingX={2}
            className={classes.pinkTitleBar}
          >
            <Typography variant="h5">
              {t('common.updateAccount')}
            </Typography>
          </Box>
          <Box paddingY={2}>
            <LoadingAndErrorFrame { ...pageFlow.status } >
              <EditManagerAccountForm
                disableAccount
                requirePasswordConfirmation
                account={defaultForm.account}
                nickname={defaultForm.nickname}
                okText={t('common.confirmUpdate')}
                onBack={handleDebouncedBack}
                onSubmit={handleSubmit}
                defaultValue={defaultForm}
              />
            </LoadingAndErrorFrame>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}

export default React.memo(ManagerAccountUpdatePage)
